import { Button, Label, Typography } from '@neo4j-ndl/react';
import type { InstanceSize, Project } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import classNames from 'classnames';

import { formatCost, getCost30DayMonth, getCurrencyForBillingMethod } from '../../utils';

type Props = {
  size?: InstanceSize;
  initialSize?: InstanceSize;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText: string;
  isLoading: boolean;
  isDisabled?: boolean;
  project: Project;
  hideCost?: boolean;
  cancelText: string;
};

export const InstanceCreateFooter = ({
  onConfirm,
  onCancel,
  size,
  initialSize,
  confirmText,
  isLoading,
  isDisabled,
  project,
  hideCost = false,
  cancelText,
}: Props) => {
  const isConfigure = isNotNullish(initialSize);
  const hasDataChanged = isConfigure ? initialSize.sizeId !== size?.sizeId : false;
  const initialCostPerHour = initialSize?.costPerHour ?? '0';
  const initialCostPerMonth = getCost30DayMonth(initialCostPerHour);
  const costPerHour = size?.costPerHour ?? '0';

  const costPerMonth = getCost30DayMonth(costPerHour);
  return (
    <div className="bg-neutral-bg-weak border-neutral-border-weak flex w-full items-center justify-center border-t p-4">
      <div className="flex w-full max-w-5xl justify-between gap-4 py-0.5">
        <div className="flex items-center gap-3">
          {!hideCost && (
            <Typography variant="h6" className="text-neutral-text-default flex flex-wrap gap-0.5">
              {isConfigure && hasDataChanged && size?.isTrial !== true && (
                <>
                  <div className="text-neutral-text-weaker line-through">
                    {formatCost(initialCostPerHour, getCurrencyForBillingMethod(project.billingMethod))}/hour
                  </div>{' '}
                  →{' '}
                </>
              )}
              {size?.isTrial === true ? (
                <Typography variant="subheading-small">
                  <Label color="discovery" fill="outlined" className="normal-case">
                    14 days free trial
                  </Label>
                </Typography>
              ) : (
                <div>{formatCost(costPerHour, getCurrencyForBillingMethod(project.billingMethod))}/hour</div>
              )}
            </Typography>
          )}
          <Typography variant="body-small" className="text-neutral-text-weak flex shrink grow flex-col flex-wrap">
            {!hideCost ? (
              <>
                <div>
                  {size?.isTrial === true ? (
                    <>
                      <span>Explore Neo4j Aura Pro with full access to advanced features.</span>
                      <br />
                      <span>Build, scale, and test your graph applications - free for 14 days.</span>
                    </>
                  ) : (
                    <>
                      <div>You will be billed hourly and can terminate your instance at any time.</div>
                      Estimated monthly cost is{' '}
                      <span
                        className={classNames({
                          'line-through': isConfigure && hasDataChanged,
                        })}
                      >
                        {formatCost(
                          isConfigure && hasDataChanged ? initialCostPerMonth : costPerMonth,
                          getCurrencyForBillingMethod(project.billingMethod),
                        )}
                      </span>
                      {isConfigure && hasDataChanged && initialCostPerHour !== costPerHour ? (
                        <span className="text-neutral-text-default">
                          {' '}
                          → {formatCost(costPerMonth, getCurrencyForBillingMethod(project.billingMethod))}
                        </span>
                      ) : (
                        <></>
                      )}{' '}
                      when continuously running for 30 days.
                    </>
                  )}
                </div>
              </>
            ) : (
              <div>Please refer to your contract for pricing.</div>
            )}
          </Typography>
        </div>
        <div className="flex items-center gap-4">
          <Button onClick={onCancel} color="neutral" fill="outlined" isDisabled={isLoading}>
            {cancelText}
          </Button>
          <Button
            onClick={onConfirm}
            color="primary"
            className="shrink-0"
            isLoading={isLoading}
            isDisabled={isDisabled}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
};
